import { iOrder } from "core/kangarooTypes/iOrder";
import { iPaginationResponse } from "core/kangarooTypes/iPaginationResponse";
import { getOrders } from "core/Services/MerchantServices";
import {
  ChevronDown,
  Download,
  Eye,
  PlusCircle,
  Search,
  X,
  ArrowDown,
  ArrowUp,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Skeleton from "react-loading-skeleton";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "shared/Button";
import FormInput from "shared/FormInput";
import { formatDate, formatStatus } from "utils/DatesUtil";
import AssignDriverDialog from "./AssignDriverDialog";
import { getStatusColors } from "utils/statusColors";
import { useTranslation } from "react-i18next";

const OperatorDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [orders, setOrders] = useState<iPaginationResponse<iOrder>>();
  const [filters, setFilters] = useState({
    order: "",
    status: "",
    orderType: "",
    dateFrom: "",
    dateTo: "",
    page: 1,
    date_type: "created_at",
    sort_field: "",
    sort_dir: "",
  });
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState<number[]>([]);

  type FiltersType = {
    order: string;
    status: string;
    orderType: string;
    dateFrom: string;
    dateTo: string;
    page: number;
    date_type: string;
    sort_field: string;
    sort_dir: string;
  };

  const cleanFilters = (filters: FiltersType) => {
    return Object.entries(filters).reduce((acc, [key, value]) => {
      if (value !== "" && value !== undefined && value !== null) {
        return { ...acc, [key]: value };
      }
      return acc;
    }, {});
  };

  const handleAssignSelectedOrders = () => {
    setIsAssignDialogOpen(true);
  };

  const toggleOrderSelection = (orderId: number) => {
    setSelectedOrders((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleSelectAllOrders = () => {
    if (!orders?.data) return;
    if (selectedOrders.length === orders.data.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(orders.data.map((order) => order.id));
    }
  };

  const fetchOrders = async (newFilters: typeof filters) => {
    try {
      setIsLoading(true);
      const cleanedFilters = cleanFilters(newFilters);
      const response = await getOrders(cleanedFilters, newFilters.page);
      if (response) {
        setOrders(response);
      }
    } catch (error) {
      toast.error("Failed to load orders.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders(filters);
  }, [filters]);

  const {
    register,
    formState: { errors },
    watch,
    reset,
  } = useForm({
    defaultValues: {
      order: "",
      status: "",
      orderType: "",
      dateFrom: "",
      dateTo: "",
      date_type: "created_at",
    },
  });

  const handleClearFilters = () => {
    reset();
    setFilters({
      order: "",
      status: "",
      orderType: "",
      dateFrom: "",
      dateTo: "",
      page: 1,
      date_type: "created_at",
      sort_field: "",
      sort_dir: "",
    });
  };

  useEffect(() => {
    const subscription = watch((values) => {
      const newFilters = {
        ...filters,
        ...values,
        page: 1, // Reset to first page when filters change
      };

      setFilters(newFilters);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({
      ...prev,
      page,
    }));
  };

  const orderTypeOptions = [
    {
      value: "fulfillment",
      label: t("operatorDashboard.orderTypes.fulfillment"),
    },
    { value: "delivery", label: t("operatorDashboard.orderTypes.delivery") },
  ];

  // Fulfillment workflow statuses
  const statusOptions = [
    {
      value: "ready_for_packaging",
      label: t("operatorDashboard.statuses.readyForPackaging"),
    },
    { value: "on_hold", label: t("operatorDashboard.statuses.onHold") },
    {
      value: "ready_for_shipping",
      label: t("operatorDashboard.statuses.readyForShipping"),
    },
    { value: "shipped", label: t("operatorDashboard.statuses.shipped") },
    {
      value: "ship_partially",
      label: t("operatorDashboard.statuses.shipPartially"),
    },
    { value: "handed_off", label: t("operatorDashboard.statuses.handedOff") },
    { value: "canceled", label: t("operatorDashboard.statuses.canceled") },
  ];

  const handleOrderDetails = (id: number) => {
    navigate(`/order-details/:id=${id}?pageTitle=Operator Order Details`);
  };

  const handleExportCSV = async () => {
    try {
      const cleanedFilters = cleanFilters(filters);
      await getOrders({ ...cleanedFilters, export: true }, filters.page);
    } catch (error) {
      toast.error("Failed to export orders.");
    }
  };

  const getStatusColorClasses = (status: string) => {
    const colors = getStatusColors(status);
    return `${colors.bg} ${colors.text}`;
  };

  const handleDialogClose = () => {
    setIsAssignDialogOpen(false);
    setSelectedOrders([]);
    fetchOrders(filters);
  };

  const handleSort = (field: string) => {
    setFilters((prev) => ({
      ...prev,
      sort_field: field,
      sort_dir:
        prev.sort_field === field && prev.sort_dir === "asc" ? "desc" : "asc",
    }));
  };

  const SortHeader = ({ field, label }: { field: string; label: string }) => {
    const isActive = filters.sort_field === field;
    return (
      <th
        onClick={() => handleSort(field)}
        className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
      >
        <div className="flex items-center space-x-1">
          <span>{label}</span>
          <div className="flex flex-col">
            {isActive && filters.sort_dir === "asc" ? (
              <ArrowUp size={14} />
            ) : null}
            {isActive && filters.sort_dir === "desc" ? (
              <ArrowDown size={14} />
            ) : null}
            {!isActive ? (
              <ArrowDown size={14} className="text-gray-300" />
            ) : null}
          </div>
        </div>
      </th>
    );
  };

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            icon={<Download size={16} />}
            onClick={handleExportCSV}
            className="w-full sm:w-auto"
          >
            {t("operatorDashboard.exportCSV")}
          </Button>
          <Button
            icon={<PlusCircle size={16} />}
            onClick={handleAssignSelectedOrders}
            className="w-full sm:w-auto"
            disabled={selectedOrders.length === 0}
          >
            {t("operatorDashboard.assignSelected")} ({selectedOrders.length})
          </Button>
        </div>
      </div>
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex justify-between mb-4">
          <h2 className="text-lg font-medium">
            {t("operatorDashboard.filters")}
          </h2>
          <Button onClick={handleClearFilters} icon={<X size={16} />}>
            {t("operatorDashboard.clearFilters")}
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <FormInput
              name="order"
              type="text"
              leftIcon={<Search size={20} />}
              placeholder={t("operatorDashboard.searchOrders")}
              register={register}
              errors={errors}
              label={t("operatorDashboard.search")}
            />
          </div>
          <div>
            <FormInput
              name="status"
              type="select"
              icon={<ChevronDown size={20} />}
              placeholder={t("operatorDashboard.status")}
              register={register}
              errors={errors}
              selectOptions={statusOptions}
              label={t("operatorDashboard.status")}
            />
          </div>
          <div>
            <FormInput
              name="orderType"
              type="select"
              icon={<ChevronDown size={20} />}
              placeholder={t("operatorDashboard.orderType")}
              register={register}
              errors={errors}
              selectOptions={orderTypeOptions}
              label={t("operatorDashboard.orderType")}
            />
          </div>
          <div>
            <FormInput
              name="date_type"
              type="select"
              icon={<ChevronDown size={20} />}
              placeholder={t("operatorDashboard.dateType")}
              register={register}
              errors={errors}
              selectOptions={[
                {
                  value: "created_at",
                  label: t("operatorDashboard.orderDate"),
                },
                {
                  value: "delivery_date",
                  label: t("operatorDashboard.deliveryDate"),
                },
              ]}
              label={t("operatorDashboard.dateType")}
            />
          </div>

          <div>
            <FormInput
              name="dateFrom"
              type="date"
              icon={<ChevronDown size={20} />}
              placeholder={t("operatorDashboard.from")}
              register={register}
              errors={errors}
              label={t("operatorDashboard.from")}
            />
          </div>
          <div>
            <FormInput
              name="dateTo"
              type="date"
              icon={<ChevronDown size={20} />}
              placeholder={t("operatorDashboard.to")}
              register={register}
              errors={errors}
              label={t("operatorDashboard.to")}
            />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
        {isLoading ? (
          [...Array(8)].map((_, index) => (
            <div
              key={index}
              className="bg-white p-4 rounded-lg border border-gray-200"
            >
              <div className="text-gray-600 text-sm mb-2">
                <Skeleton width={80} />
              </div>
              <div className="text-2xl">
                <Skeleton width={40} />
              </div>
            </div>
          ))
        ) : (
          <>
            {[
              {
                label: t("operatorDashboard.summaryCards.readyForPackaging"),
                value: orders?.ready_for_packaging,
                color: "border-l-4 border-l-blue-500",
              },
              {
                label: t("operatorDashboard.summaryCards.onHold"),
                value: orders?.on_hold,
                color: "border-l-4 border-l-yellow-500",
              },
              {
                label: t("operatorDashboard.summaryCards.readyForShipping"),
                value: orders?.ready_for_shipping,
                color: "border-l-4 border-l-indigo-500",
              },
              {
                label: t("operatorDashboard.summaryCards.shipped"),
                value: orders?.shipped,
                color: "border-l-4 border-l-green-500",
              },
              {
                label: t("operatorDashboard.summaryCards.shipPartially"),
                value: orders?.ship_partially,
                color: "border-l-4 border-l-orange-500",
              },
              {
                label: t("operatorDashboard.summaryCards.handedOff"),
                value: orders?.handed_off,
                color: "border-l-4 border-l-purple-500",
              },
              {
                label: t("operatorDashboard.summaryCards.delivered"),
                value: orders?.delivered,
                color: "border-l-4 border-l-emerald-500",
              },
              {
                label: t("operatorDashboard.summaryCards.canceled"),
                value: orders?.canceled,
                color: "border-l-4 border-l-red-500",
              },
            ].map((card, index) => (
              <div
                key={index}
                className={`bg-white p-4 rounded-lg border border-gray-200 ${card.color}`}
              >
                <div className="text-gray-600 text-sm">{card.label}</div>
                <div className="text-2xl mt-1">{card.value ?? 0}</div>
              </div>
            ))}
          </>
        )}
      </div>
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full hidden md:table">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <input
                  type="checkbox"
                  checked={
                    selectedOrders.length > 0 &&
                    orders?.data &&
                    selectedOrders.length === orders.data.length
                  }
                  onChange={handleSelectAllOrders}
                  className="rounded"
                />
              </th>
              <SortHeader
                field="id"
                label={t("operatorDashboard.table.orderId")}
              />
              <SortHeader
                field="merchant_name"
                label={t("operatorDashboard.table.merchant")}
              />
              <SortHeader
                field="customer_name"
                label={t("operatorDashboard.table.customer")}
              />
              <SortHeader
                field="driver_name"
                label={t("operatorDashboard.table.driver")}
              />
              <SortHeader
                field="type"
                label={t("operatorDashboard.table.type")}
              />
              <SortHeader
                field="status"
                label={t("operatorDashboard.table.status")}
              />
              <SortHeader
                field="total"
                label={t("operatorDashboard.table.amount")}
              />
              <SortHeader
                field="created_at"
                label={t("operatorDashboard.table.orderDate")}
              />
              <SortHeader
                field="delivery_date"
                label={t("operatorDashboard.table.deliveryDate")}
              />
              <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("operatorDashboard.table.actions")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading
              ? [...Array(5)].map((_, index) => (
                  <tr key={index}>
                    <td className="p-3">
                      <Skeleton width={20} />
                    </td>
                    {[...Array(10)].map((_, colIndex) => (
                      <td key={colIndex} className="p-3">
                        <Skeleton width={80} />
                      </td>
                    ))}
                  </tr>
                ))
              : orders?.data.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="p-3">
                      <input
                        type="checkbox"
                        checked={selectedOrders.includes(order.id)}
                        onChange={() => toggleOrderSelection(order.id)}
                        className="rounded"
                      />
                    </td>
                    <td className="p-3">{order.id}</td>
                    <td className="p-3">{order.merchant_name}</td>
                    <td className="p-3">{order.customer_name}</td>
                    <td className="p-3">
                      {order.driver_name === "" ? "--" : order.driver_name}
                    </td>
                    <td className="p-3">{order.type}</td>
                    <td className="p-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${getStatusColorClasses(
                          order.status
                        )}`}
                      >
                        {formatStatus(order.status)}
                      </span>
                    </td>
                    <td className="p-3">{order.total}</td>
                    <td className="p-3">{formatDate(order.created_at)}</td>
                    <td className="p-3">{formatDate(order.delivery_date)}</td>
                    <td className="p-3 flex gap-2 items-center">
                      <button
                        onClick={() => {
                          setSelectedOrders([order.id]);
                          setIsAssignDialogOpen(true);
                        }}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        <PlusCircle size={18} />
                      </button>
                      <button
                        onClick={() => handleOrderDetails(order.id)}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        <Eye size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
        <div className="md:hidden divide-y">
          {isLoading
            ? [...Array(5)].map((_, index) => (
                <div key={index} className="p-4">
                  <Skeleton height={100} />
                </div>
              ))
            : orders?.data.map((order) => (
                <div
                  key={order.id}
                  className="p-4 flex justify-between items-start hover:bg-gray-50"
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedOrders.includes(order.id)}
                      onChange={() => toggleOrderSelection(order.id)}
                      className="rounded"
                    />
                    <div>
                      <div className="font-medium">
                        {t("operatorDashboard.table.orderId")} #{order.id}
                      </div>
                      <div className="text-sm text-gray-500">
                        {t("operatorDashboard.table.customer")}:{" "}
                        {order.customer_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {t("operatorDashboard.table.merchant")}:{" "}
                        {order.merchant_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {t("operatorDashboard.table.driver")}:{" "}
                        {order.driver_name || "--"}
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatDate(order.created_at)}
                      </div>
                      <span
                        className={`inline-block mt-2 px-2 py-1 rounded-full text-xs ${getStatusColorClasses(
                          order.status
                        )}`}
                      >
                        {formatStatus(order.status)}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">JOD {order.total}</div>
                    <div className="flex gap-2 items-center">
                      <button
                        onClick={() => {
                          setSelectedOrders([order.id]);
                          setIsAssignDialogOpen(true);
                        }}
                        className="text-gray-600 hover:text-gray-900 mt-2"
                      >
                        <PlusCircle size={18} />
                      </button>
                      <button
                        onClick={() => handleOrderDetails(order.id)}
                        className="text-gray-600 hover:text-gray-900 mt-2"
                      >
                        <Eye size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
        </div>
        <div className="bg-white px-4 py-3 flex flex-col sm:flex-row items-center justify-between border-t border-gray-200">
          <div className="text-sm text-gray-500 mb-2 sm:mb-0">
            {t("operatorDashboard.pagination.showing")} {orders?.meta.from}-
            {orders?.meta.to} {t("operatorDashboard.pagination.of")}{" "}
            {orders?.meta.total} {t("operatorDashboard.pagination.entries")}
          </div>
          <div className="flex space-x-2">
            <button
              className="px-3 py-1 border rounded text-sm disabled:opacity-50"
              disabled={filters.page === 1}
              onClick={() => handlePageChange(filters.page - 1)}
            >
              {t("operatorDashboard.pagination.previous")}
            </button>
            <button className="px-3 py-1 border rounded text-sm bg-blue-600 text-white">
              {filters.page}
            </button>
            <button
              className="px-3 py-1 border rounded text-sm disabled:opacity-50"
              disabled={filters.page === orders?.meta.last_page}
              onClick={() => handlePageChange(filters.page + 1)}
            >
              {t("operatorDashboard.pagination.next")}
            </button>
          </div>
        </div>
        <AssignDriverDialog
          isOpen={isAssignDialogOpen}
          onClose={handleDialogClose}
          orderIds={selectedOrders}
        />
      </div>
    </div>
  );
};

export default OperatorDashboard;
